import {
  ArrowButtonLeftBold,
  ArrowButtonRightBold,
  PictureSunBold,
  RealEstateDimensionsBlockBold,
  VideoPlayerSliderBold,
} from '@ha/icons';
import React from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles, fade } from '@material-ui/core';
import cx from 'classnames';

import { useIntl } from 'ha/i18n';
import {
  PaperBadge as Badge,
  Grid,
  IconButton,
  units,
  Typography,
} from '@ha/dsl';
import LazyComponent from 'ha/components/LazyComponent';
import FavoriteButton from 'ha/components/next/FavoriteButton';
import { useConfig } from 'ha/helpers/config';
import { transformImageUrl } from 'ha/modules/Image/transformImageUrl';
import { ImageSize } from 'ha/modules/Image/constants';
import { useFavoriteListing } from 'ha/modules/FavoriteListing';
import { track } from 'ha/modules/Analytics/actions';
import { useFeatureFlags } from 'ha/modules/FeatureFlags';
import getFirstAvailableDay from 'ha/utils/exclusionDate/firstAvailableDay';
import Img from 'ha/ui/next/Img';
import { Imgix } from 'ha/modules/Image/Imgix';
import { SlideRendererParams } from 'ha/components/next/Slider';
import { VirtualizeSlider } from 'ha/components/next/Slider/VirtualizeSlider';
import { NormalizedAlgoliaListing } from 'ha/models/Listing/types';

import { ListingCancellationPolicy } from 'ha/services/apiV2/types/Listing';

interface Props {
  room: NormalizedAlgoliaListing;
  isLazy?: boolean;
  scrollableElement?: HTMLElement | 'window';
  bottomOffset?: string;
  withGallery?: boolean;
  withFavorites?: boolean;
  // used in analytics
  cardLocation?: string;
}

const useStyles = makeStyles(theme => ({
  media: {
    position: 'relative',
    width: '100%',
    paddingTop: '66.66%' /* 3:2 Aspect Ratio */,
  },
  moreMediaInfo: {
    marginTop: units.rem(1),
    color: theme.palette.white.main,
  },
  moreMediaText: {
    textDecoration: 'underline',
  },
  moreMediaIcon: {
    marginRight: units.rem(0.5),
    marginLeft: units.rem(1),
  },
  moreMediaIconFirst: {
    marginLeft: 0,
  },
  absolute: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  content: {
    padding: theme.spacing(1.5),
  },
  interactable: {
    zIndex: 2,
  },
  imageContainer: {
    position: 'relative',
    overflow: 'hidden',
    height: '100%',
  },
  arrowButton: {
    color: theme.palette.white.main,
    filter: `drop-shadow(0px 1px 3px ${theme.palette.grey[700]})`,
    padding: units.rem(0.5),
  },
  arrowButtonDisabled: {
    color: fade(theme.palette.white.main, 0.5),
  },
  favoriteButton: {
    marginLeft: 'auto',
  },
  imageCover: {
    objectFit: 'cover',
    height: '100%',
    width: '100%',
  },
  fullHeight: {
    height: '100%',
  },
  badge: {
    background: theme.palette.white.main,
    color: theme.palette.orange.dark,
    borderRadius: units.rem(0.875),
    fontSize: units.rem(0.875),
    padding: units.yx(0.18, 0.55),
  },
  cancellationBadge: {
    color: theme.palette.grey['700'],
  },
  moreMediaOverlay: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: fade(theme.palette.grey[700], 0.65),
  },
}));

const MaxPhotoCount = 5;

const PhotoGalleryWithControls: React.FC<Props> = ({
  room: {
    id,
    photos,
    exclusions,
    minimalRentalPeriod,
    photoCount,
    floorPlanCount,
    videoCount,
    previewImage,
    street,
    city,
    isNew,
    cPolicy,
  },
  isLazy,
  scrollableElement,
  bottomOffset,
  cardLocation,
  withGallery,
  withFavorites,
}) => {
  const dispatch = useDispatch();
  const { T, formatDate } = useIntl();
  const classes = useStyles();
  const appConfig = useConfig();

  const { imgix } = useFeatureFlags();

  const imageTimeoutMs = appConfig?.thumb?.imageTimeoutMs;

  const {
    onClick: onFavoriteClick,
    isLoading: isFavoriteLoading,
    isFavorite,
  } = useFavoriteListing(id);

  const [index, setIndex] = React.useState(0);

  const slideCount = Math.min(photos?.length || 0, MaxPhotoCount);

  const isFirstSlide = index === 0;
  const isLastSlide = index === slideCount - 1;

  const isCancellationFlexible =
    cPolicy === ListingCancellationPolicy.FLEXIBLE_30D;
  const showMoreMediaSlide =
    index === slideCount - 1 && photoCount > MaxPhotoCount;

  const getTransformedImageUrl = React.useCallback(
    (imageUrl: string) =>
      transformImageUrl(imageUrl, {
        size: ImageSize.small,
        transformation: 'crop',
      }),
    [transformImageUrl],
  );

  const handleFavoritesClick = React.useCallback(
    (e: React.MouseEvent) => {
      onFavoriteClick(e);
      dispatch(
        track('Favorite clicked', {
          type: 'button',
          listingId: id,
          favoriteType: isFavorite ? 'unfavorite' : 'favorite',
        }),
      );
    },
    [dispatch, onFavoriteClick, track, id, isFavorite],
  );

  const handlePrevIndex = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      if (!isFirstSlide) {
        setIndex(index - 1);
      }
    },
    [index, isFirstSlide],
  );

  const handleNextIndex = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      if (!isLastSlide) {
        setIndex(index + 1);
      }
    },
    [index, isLastSlide],
  );

  const handleOverlayClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      dispatch(track('more photos available clicked', { cardLocation }));
    },
    [dispatch, track, cardLocation],
  );

  const handleChangeIndex = React.useCallback((newIndex: number) => {
    setIndex(newIndex);
  }, []);

  const listingDatesLabel = React.useMemo(() => {
    const firstAvailableDay = getFirstAvailableDay(
      exclusions,
      minimalRentalPeriod,
    );

    return firstAvailableDay
      ? T('Available from %(dates)s', {
          dates: formatDate('DD MMM YYYY', firstAvailableDay),
        })
      : false;
  }, [getFirstAvailableDay, exclusions, minimalRentalPeriod, formatDate]);

  const hasGallery = Boolean(withGallery && photos?.length);

  // eslint-disable-next-line no-nested-ternary
  const listingPicture = hasGallery ? (
    <VirtualizeSlider
      overscanSlideAfter={isLastSlide ? 0 : 1}
      overscanSlideBefore={isFirstSlide ? 0 : 1}
      slideCount={slideCount}
      style={{ height: '100%' }}
      containerStyle={{ height: '100%' }}
      index={index}
      onChangeIndex={handleChangeIndex}
      slideRenderer={({ index: currentIndex, key }: SlideRendererParams) => {
        const image = photos?.[currentIndex];

        if (!image) {
          return null;
        }

        return (
          <div className={classes.imageContainer} key={key}>
            {imgix === 'on' && (
              <Imgix
                className={classes.imageCover}
                height={300}
                htmlAttributes={{ role: 'presentation', loading: 'lazy' }}
                src={image}
                width={490}
              />
            )}
            {imgix !== 'on' && (
              <Img
                itemProp="image"
                className={cx(classes.imageCover)}
                src={getTransformedImageUrl(image)}
                timeoutMs={imageTimeoutMs}
                isResponsive
                role="presentation"
                loading="lazy"
              />
            )}
            {currentIndex === slideCount - 1 && photoCount > MaxPhotoCount && (
              <Grid
                container
                justify="center"
                alignItems="center"
                direction="column"
                onClick={handleOverlayClick}
                className={classes.moreMediaOverlay}
              >
                <Typography.Paragraph
                  color="white"
                  className={classes.moreMediaText}
                >
                  {T('listing_card.last_slide_media')}
                </Typography.Paragraph>
                <Grid
                  container
                  className={classes.moreMediaInfo}
                  alignItems="center"
                  justify="center"
                >
                  {photoCount > 0 && (
                    <React.Fragment>
                      <PictureSunBold
                        fontSize="inherit"
                        className={cx(
                          classes.moreMediaIcon,
                          classes.moreMediaIconFirst,
                        )}
                      />
                      {photoCount}
                    </React.Fragment>
                  )}
                  {floorPlanCount > 0 && (
                    <React.Fragment>
                      <RealEstateDimensionsBlockBold
                        fontSize="inherit"
                        className={classes.moreMediaIcon}
                      />
                      {floorPlanCount}
                    </React.Fragment>
                  )}
                  {videoCount > 0 && (
                    <React.Fragment>
                      <VideoPlayerSliderBold
                        fontSize="inherit"
                        className={classes.moreMediaIcon}
                      />
                      {videoCount}
                    </React.Fragment>
                  )}
                </Grid>
              </Grid>
            )}
          </div>
        );
      }}
    />
  ) : imgix === 'on' ? (
    <Imgix
      src={previewImage}
      htmlAttributes={{
        alt: `${listingDatesLabel} (${street}, ${city})`,
        itemProp: 'image',
        role: 'presentation',
        loading: 'lazy',
        'data-test-locator': 'ListingCard Photo',
      }}
      height={300}
      width={490}
      className={classes.imageCover}
    />
  ) : (
    <Img
      itemProp="image"
      src={getTransformedImageUrl(previewImage)}
      alt={`${listingDatesLabel} (${street}, ${city})`}
      timeoutMs={imageTimeoutMs}
      className={cx(classes.imageCover)}
      isResponsive
      role="presentation"
      dataTestLocator="ListingCard Photo"
    />
  );

  return (
    <div className={classes.media}>
      <div
        className={cx(
          classes.absolute,
          classes.fullHeight,
          classes.interactable,
        )}
      >
        {isLazy ? (
          <LazyComponent
            scrollableAncestor={scrollableElement}
            bottomOffset={bottomOffset}
          >
            {listingPicture}
          </LazyComponent>
        ) : (
          listingPicture
        )}
      </div>
      <Grid
        container
        alignContent="center"
        className={cx(classes.absolute, classes.content)}
      >
        {hasGallery && (
          <Grid item container justify="space-between">
            <IconButton
              disableRipple={isFirstSlide}
              component="div"
              edge="start"
              aria-label={T('Previous image')}
              className={cx(
                classes.arrowButton,
                classes.interactable,
                isFirstSlide && classes.arrowButtonDisabled,
              )}
              onClick={handlePrevIndex}
            >
              <ArrowButtonLeftBold />
            </IconButton>
            <IconButton
              disableRipple={isLastSlide}
              component="div"
              edge="end"
              aria-label={T('Next image')}
              className={cx(
                classes.arrowButton,
                classes.interactable,
                isLastSlide && classes.arrowButtonDisabled,
              )}
              onClick={handleNextIndex}
            >
              <ArrowButtonRightBold />
            </IconButton>
          </Grid>
        )}
      </Grid>
      <Grid
        className={cx(classes.absolute, classes.content)}
        container
        direction="column"
        justify="space-between"
      >
        <Grid item container justify="space-between">
          {isCancellationFlexible && !showMoreMediaSlide && (
            <Grid item className={classes.interactable}>
              <Badge className={cx(classes.badge, classes.cancellationBadge)}>
                {T('search.cancellation_policy.tag.flexible_cancellation')}
              </Badge>
            </Grid>
          )}
          {isNew && !isCancellationFlexible && !showMoreMediaSlide && (
            <Grid item className={classes.interactable}>
              <Badge className={classes.badge}>{T('New')}</Badge>
            </Grid>
          )}
          {withFavorites && (
            <Grid
              item
              className={cx(classes.favoriteButton, classes.interactable)}
            >
              <FavoriteButton
                size="xs"
                onClick={handleFavoritesClick}
                isLoading={isFavoriteLoading}
                isFavorite={isFavorite}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </div>
  );
};

export default PhotoGalleryWithControls;
